import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Badge from "react-bootstrap/Badge";

import Form from "../../components/form/Form";
import { getCategories } from "../../services/CategoryService";
import {
  createEmployment,
  updateEmployment,
  updateEmploymentByEmployee,
} from "../../services/EmploymentService";
import yup from "../../utils/yup";
import { CURRENCIES, EmploymentStatuses, Roles } from "../../utils/constants";
import { getCurrentUser } from "../../utils/utils";
import AppModal from "../../components/Modal";

const EmploymentFormModel = ({
  open,
  onClose,
  setEmployments,
  employment,
  inviteCode,
}) => {
  const [employmentFormDetails, setEmploymentFormDetails] = useState(null);
  const user = getCurrentUser();

  useEffect(() => {
    getCategories(`created_by=${user.id}`).then((data) => {
      setEmploymentFormDetails({
        submitButtonLabel: "Submit",
        steps: [
          {
            name: "identifier",
            label: "Email, phone number or invite code",
            type: "text",
            disabled: !!employment,
          },
          {
            name: "categories",
            label: "Select work categories for the employee",
            type: "select-text-input",
            creatable: true,
            multiple: true,
            options: data
              ? data.map((cat) => ({
                  value: cat.name,
                  label: cat.name,
                }))
              : [],
            helpText:
              "Select the category or add a new one by typing in the bar.",
          },
          ...(user.role === Roles.EMPLOYEE.value
            ? [
                {
                  name: "hourly_rate",
                  label: "Hourly rate",
                  htmlType: "number",
                },
                {
                  name: "hourly_rate_currency",
                  label: "Currency",
                  type: "select-text-input",
                  options: Object.keys(CURRENCIES).map((key) => ({
                    label: `${CURRENCIES[key].name} - ${CURRENCIES[key].code}`,
                    value: CURRENCIES[key].code,
                  })),
                },
              ]
            : []),
          // {
          //   name: "is_private",
          //   type: "checkbox",
          //   defaultValue: true,
          //   label: `Is the ${
          //     user.role === Roles.EMPLOYER.value ? "employee" : "employer"
          //   } private?`,
          //   helpText: `Should this ${
          //     user.role === Roles.EMPLOYER.value ? "employee" : "employer"
          //   } be private and not viewable by the opposite party.`,
          // },
        ],
      });
    });
  }, [employment]);

  const employmentFormSchema = yup
    .object()
    .shape({
      identifier: yup
        .string()
        .typeError("The email, phone number or invite code is not valid.")
        .required("An email, phone number or invite code is required.")
        .test(
          "identifier-check",
          "The email, phone number or invite code is not valid.",
          (value) => {
            let shouldRaiseError = false;

            // Check for email
            if (
              !RegExp("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$").test(
                value
              )
            ) {
              shouldRaiseError = true;
            }

            // Check for phone number
            if (
              !RegExp(
                "^((\\+[1-9]{1,4}[ -]?)|(\\([0-9]{2,3}\\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$"
              ).test(value)
            ) {
              shouldRaiseError = true;
            }

            // Check for UUID
            if (
              !RegExp(
                "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$"
              ).test(value)
            ) {
              shouldRaiseError = true;
            }

            return shouldRaiseError;
          }
        ),
      ...(user.role === Roles.EMPLOYEE.value
        ? {
            hourly_rate: yup
              .number()
              .typeError("The hourly rate is not valid.")
              .nullable(),
            hourly_rate_currency: yup
              .string()
              .typeError("The hourly rate currency is not valid.")
              .test(
                "check-hourly-rate-currency",
                "The hourly rate currency is not a valid choice.",
                (val) => Object.keys(CURRENCIES).includes(val)
              )
              .nullable(),
          }
        : {}),
      is_private: yup
        .bool()
        .typeError("The is private field is not valid.")
        .default(false),
      categories: yup.array().default([]),
    })
    .required();

  const employmentForm = useForm({
    resolver: yupResolver(employmentFormSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (inviteCode) {
      employmentForm.setValue("identifier", inviteCode);
    }
  }, [inviteCode]);

  useEffect(() => {
    if (employment) {
      const form =
        employment.created_by_id === user.id
          ? employmentForm
          : statusEmploymentForm;
      Object.keys(employment).forEach((k) => {
        if (
          (k === "identifier" && !employment[k]) ||
          (k === "user" && employment[k])
        ) {
          form.setValue("identifier", employment["user"].email);
        } else if (k === "categories") {
          form.setValue(
            k,
            employment[k].map((i) => i.name)
          );
        } else {
          form.setValue(k, employment[k]);
        }
      });
    } else {
      statusEmploymentForm.reset();
      employmentForm.reset();
    }
  }, [employment]);

  const employmentOnSubmit = (values) => {
    if (employment) {
      updateEmployment(employmentForm.setError)(values).then((data) => {
        toast.success(
          `The ${
            user.role === Roles.EMPLOYER.value ? "employee" : "employer"
          } has been updated`
        );
        setEmployments((oldVal) =>
          oldVal.map((i) => {
            if (i.id === employment.id) {
              return data;
            }
            return i;
          })
        );
        onClose();
        employmentForm.reset();
      });
    } else {
      createEmployment(employmentForm.setError)(values).then((data) => {
        toast.success(
          `The ${
            user.role === Roles.EMPLOYER.value ? "employee" : "employer"
          } has been created and needs to be accepted.`
        );
        setEmployments((oldVal) => [...oldVal, data]);
        onClose();
        employmentForm.reset();
      });
    }
  };

  const statusEmploymentFormSchema = yup
    .object()
    .shape({
      status: yup.string().typeError("The status is not valid."),
      hourly_rate: yup
        .number()
        .transform((val) => {
          if (isNaN(val)) return null;
          return val;
        })
        .typeError("The hourly rate is not valid.")
        .nullable(),
      hourly_rate_currency: yup
        .string()
        .typeError("The hourly rate currency is not valid.")
        .test(
          "check-hourly-rate-currency",
          "The hourly rate currency is not a valid choice.",
          (val) => {
            if (!val) {
              return !val;
            }
            return Object.keys(CURRENCIES).includes(val);
          }
        )
        .nullable(),
    })
    .required();

  const statusEmploymentSteps = {
    submitButtonLabel: "Submit",
    steps: [
      {
        name: "status",
        label: "Employment Status",
        type: "select",
        options: Object.keys(EmploymentStatuses)
          .filter(
            (i) =>
              EmploymentStatuses[i].value !== EmploymentStatuses.PENDING.value
          )
          .map((i) => EmploymentStatuses[i]),
      },
      {
        name: "hourly_rate",
        label: "Hourly rate",
        htmlType: "number",
      },
      {
        name: "hourly_rate_currency",
        label: "Currency",
        type: "select-text-input",
        options: Object.keys(CURRENCIES).map((key) => ({
          label: `${CURRENCIES[key].name} - ${CURRENCIES[key].code}`,
          value: CURRENCIES[key].code,
        })),
      },
    ],
  };

  const statusEmploymentForm = useForm({
    resolver: yupResolver(statusEmploymentFormSchema),
    mode: "onChange",
  });

  const employmentEmployeeOnSubmit = (values) => {
    updateEmploymentByEmployee(statusEmploymentSteps.setError)({
      employmentId: employment.id,
      ...values,
    }).then((data) => {
      toast.success("The employer has been updated.");
      setEmployments((oldVal) =>
        oldVal.map((i) => {
          if (i.id === employment.id) {
            return { ...i, ...data };
          }
          return i;
        })
      );
      onClose();
    });
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={`${employment ? "Update" : "Invite"} an ${
        user.role === Roles.EMPLOYER.value ? "employee" : "employer"
      }`}
    >
      {!employment || user.id === employment?.created_by_id ? (
        <Form
          formDetails={employmentFormDetails}
          form={employmentForm}
          onSubmit={employmentOnSubmit}
          additionalButtons={
            <Button onClick={onClose} className="mt-4" variant="outline-danger">
              Cancel
            </Button>
          }
        />
      ) : (
        <Container>
          <div className="text-center mb-3">
            <h5>Categories</h5>
            {employment?.categories.map((i) => (
              <Badge
                key={i.name}
                className="me-2 mb-2"
                style={{
                  fontSize: "14px",
                  backgroundColor: "rgba(37, 83, 126, 1) !important",
                }}
              >
                {i.name}
              </Badge>
            ))}
          </div>
          <Form
            formDetails={statusEmploymentSteps}
            form={statusEmploymentForm}
            onSubmit={employmentEmployeeOnSubmit}
            additionalButtons={
              <Button
                onClick={onClose}
                className="mt-4"
                variant="outline-danger"
              >
                Cancel
              </Button>
            }
          />
        </Container>
      )}
    </AppModal>
  );
};

export default EmploymentFormModel;
